<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConsentStore } from '@/entities/consent'
import CCookiePreferencesDialog from './CCookiePreferencesDialog.vue'

/**
 * Site-wide consent banner. Rendered by the landing layout. Shows only
 * while the user has not recorded a consent decision — once they choose
 * Accept, Reject, or save granular preferences, the banner stays hidden
 * unless they later "Withdraw all" from the profile.
 *
 * GDPR alignment:
 *   - Accept and Reject are visually equivalent (same size, same row)
 *     so withdrawing is as easy as granting — Art. 7(3).
 *   - "Customize" opens the full granular form so users are not forced
 *     into an all-or-nothing choice at the banner level — Art. 7(4).
 */

const { t } = useI18n()
const consentStore = useConsentStore()

const showCustomize = ref(false)

// Hydrate synchronously in setup so the persisted decision is read
// from localStorage BEFORE first render — otherwise the banner flashes
// for one frame on every reload. The async backend-sync portion is
// intentionally not awaited.
consentStore.initialize()

// During prerendering (scripts/prerender.mjs sets window.__PRERENDER__ before
// the app boots) the banner must NOT render. It's teleported to <body>, i.e.
// OUTSIDE #app, so a prerendered copy would be baked into the static HTML and
// Vue — which only owns #app — would never remove it, leaving a dead,
// unclickable duplicate next to the live banner.
const isPrerender = typeof window !== 'undefined' && '__PRERENDER__' in window

const isVisible = computed(() => !isPrerender && !consentStore.hasDecided)

function onAcceptAll() {
  consentStore.acceptAll()
}

function onRejectAll() {
  consentStore.rejectAll()
}

function onCustomize() {
  showCustomize.value = true
}
</script>

<template>
  <Teleport to="body">
    <Transition
      name="cookie-pop"
      enter-active-class="animate-cookie-enter"
      leave-active-class="animate-cookie-leave"
    >
      <div
        v-if="isVisible"
        class="fixed bottom-4 inset-x-0 mx-auto z-50 w-[calc(100%-2rem)] max-w-2xl"
      >
        <div class="bg-gray-900 text-white rounded-2xl shadow-2xl p-5 md:p-6">
          <div class="flex items-start gap-4">
            <!-- Cookie Icon -->
            <div class="flex-shrink-0 cookie-wobble pt-0.5">
              <span class="text-3xl inline-block">🍪</span>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <h3 class="text-base md:text-lg font-semibold leading-snug mb-1">
                {{ t('cookie_banner_title') }}
              </h3>
              <p class="text-sm leading-relaxed text-gray-200">
                {{ t('cookie_banner_message') }}
              </p>
            </div>
          </div>

          <!-- Actions -->
          <div
            class="mt-4 md:mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2 sm:gap-3 sm:justify-end"
          >
            <button
              type="button"
              class="flex-shrink-0 text-sm font-medium text-gray-200 hover:text-white underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-md px-2 py-1"
              @click="onCustomize"
            >
              {{ t('cookie_customize') }}
            </button>
            <button
              type="button"
              class="flex-shrink-0 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2.5 rounded-xl font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900"
              @click="onRejectAll"
            >
              {{ t('cookie_reject_all') }}
            </button>
            <button
              type="button"
              class="flex-shrink-0 bg-[#9117EF] hover:bg-[#7b12cc] text-white px-5 py-2.5 rounded-xl font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900"
              @click="onAcceptAll"
            >
              {{ t('cookie_accept_all') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <CCookiePreferencesDialog v-model="showCustomize" />
  </Teleport>
</template>

<style scoped>
/* Entry: bounce up from bottom with slight overshoot.
 * Horizontal centering is handled by inset-x-0 + mx-auto (NOT transform), so
 * these keyframes only animate Y/scale. A persistent transform on a
 * position:fixed element breaks tap hit-testing on iOS/iPadOS Safari. */
@keyframes cookie-enter {
  0% {
    transform: translateY(100%) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: translateY(-8px) scale(1.02);
    opacity: 1;
  }
  70% {
    transform: translateY(4px) scale(0.99);
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* Exit: cookie crumbles away */
@keyframes cookie-leave {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  30% {
    transform: translateY(-10px) scale(1.05);
    opacity: 1;
  }
  100% {
    transform: translateY(30px) scale(0.7) rotate(5deg);
    opacity: 0;
  }
}

/* Cookie emoji wobble on hover */
@keyframes wobble {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-15deg);
  }
  50% {
    transform: rotate(10deg);
  }
  75% {
    transform: rotate(-5deg);
  }
}

.animate-cookie-enter {
  animation: cookie-enter 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.animate-cookie-leave {
  animation: cookie-leave 0.4s ease-in forwards;
}

.cookie-wobble:hover span {
  animation: wobble 0.5s ease-in-out;
}
</style>
